import React, {useEffect, useMemo, useState} from 'react';
import {PageStyleProps} from '@taopix/taopix-core/dist/types/types';
import LayoutThumbnail from './LayoutThumbnail';
import {useTranslation} from 'react-i18next';

type LayoutsListProps = {
  pagestyles: PageStyleProps[];
  selectedLayout: PageStyleProps | undefined;
  onClick: (selectedLayout: PageStyleProps) => void;
  onKeyDown?: (selectedLayout: PageStyleProps, event: React.KeyboardEvent) => void;
  layoutType: 'page' | 'cover';
  visible: boolean;
};

const LayoutsList = ({
  pagestyles,
  onClick,
  selectedLayout,
  onKeyDown,
  layoutType,
  visible,
  ...props
}: LayoutsListProps) => {
  const {t} = useTranslation();
  const thumbnailListClasses = [
    'flex',
    'flex-wrap',
    'justify-center',
    '-ml-sm',
    '-mt-sm',
    'items-start',
    'w-[calc(100%_+_10rem)]',
  ];

  // The base height is arbitrary - it is only used to calculate pixels widths for the thumbnail containers, which is only to create the correct size ratio as tailwind displays the thumbnails as a proportion of their container.
  const baseHeight = 109;
  const refs = React.useRef<Array<HTMLImageElement | null>>([]);

  const handleOnClick = (pagestyle: PageStyleProps, index: number) => {
    onClick(pagestyle);
  };

  const content = useMemo(() => {
    if (!visible) {
      return <></>;
    }

    if (!pagestyles.length) {
      return (
        <p className="mt-sm">
          {t(
            'page' === layoutType
              ? 'str_LabelNoPageLayoutsAvailable'
              : 'str_LabelNoCoverLayoutsAvailable'
          )}
        </p>
      );
    }

    return (
      <ul
        className={thumbnailListClasses.join(' ')}
        style={{'--gap': '10rem'} as React.CSSProperties}
        aria-label={props['aria-label']}
      >
        {pagestyles.map((pagestyle, index) => {
          return (
            <LayoutThumbnail
              key={`pagestyle-${pagestyle.ref}`}
              pagestyle={pagestyle}
              thumbnailPath={pagestyle.thumbnailPath}
              thumbnailRatio={pagestyle.thumbnailRatio}
              baseHeight={baseHeight}
              checked={pagestyle.ref === selectedLayout?.ref}
              onClick={() => handleOnClick(pagestyle, index)}
              onKeyDown={(pagestyle, event: React.KeyboardEvent) => {
                onKeyDown?.(pagestyle, event);
              }}
              ref={thumbnail => {
                refs.current[index] = thumbnail;
              }}
            />
          );
        })}
      </ul>
    );
  }, [pagestyles, visible]);

  return content;
};

export default LayoutsList;
